/**
 * Topics that make a worksheet-scoped request eligible for the aligned
 * tabular-query workload. Entries are regex fragments so the detector accepts
 * common aliases and punctuation variants without requiring exact catalog
 * labels.
 */
const TABULAR_TOPIC_PATTERNS: readonly string[] = [
  // Market data and valuation
  "52[-\\s]?week\\s+(?:high|low)",
  "(?:consensus\\s+)?target\\s+price",
  "(?:share|stock|equity)\\s+prices?",
  "prices?\\s+per\\s+share",
  "market\\s+cap(?:italization|italisation)?",
  "total\\s+return",
  "free\\s+float",
  "ev\\s*\\/\\s*(?:ebitda|ebit|fcf|sales)",
  "p\\s*\\/\\s*(?:fcf|e|b|s)",
  "price[-\\s]to[-\\s](?:earnings|book|sales|free\\s+cash\\s+flow)",

  // Per-share, growth, and shareholder metrics
  "book\\s+value\\s*(?:\\/|per)\\s*share(?:\\s+growth)?",
  "(?:cash\\s+flow|revenue)\\s*(?:\\/|per)\\s*share\\s+growth",
  "dividends?\\s*(?:\\/|per)\\s*share(?:\\s+growth)?",
  "earnings\\s+per\\s+share(?:\\s+(?:long[-\\s]?term\\s+)?growth)?",
  "eps(?:\\s+(?:long[-\\s]?term\\s+)?growth)?",
  "shares?\\s+outstanding",
  "shareholders?'?\\s+equity",
  "(?:share\\s+repurchases?|buybacks?)",
  "(?:buybacks?|share\\s+repurchases?)\\s+authori[sz]ations?",
  "payout\\s+ratio",

  // Income statement and margins
  "revenues?\\s+by\\s+segments?",
  "revenues?\\s+growth",
  "organic\\s+revenue\\s+growth",
  "revenues?",
  "gross\\s+(?:profit|margin)",
  "ebitda(?:\\s+margin)?",
  "ebit(?:\\s+margin)?",
  "operating\\s+income(?:\\s+growth)?",
  "operating\\s+margin",
  "net\\s+income(?:\\s+growth)?",
  "net\\s+margin(?:\\s+growth)?",
  "pre[-\\s]?tax\\s+(?:income|margin)",
  "effective\\s+tax\\s+rate",
  "(?:(?:current|deferred)\\s+)?tax\\s+expense",

  // Cash flow, capital expenditure, and dividends
  "(?:capex|capital\\s+expenditures?)(?:\\s+guidance)?",
  "operating\\s+cash\\s+flow",
  "free\\s+cash\\s+flow(?:\\s+yield)?",
  "cash\\s+flow\\s+roic",
  "cash\\s+dividend\\s+coverage",
  "cash\\s+dividends?\\s*(?:\\/|per)\\s*cash\\s+flow",
  "cash\\s+dividends?\\s+paid",
  "cash\\s+interest\\s+paid",
  "cash\\s+taxes\\s+paid",
  "dividend\\s+(?:policy|yield)",

  // Balance sheet, debt, and liquidity
  "cash\\s*(?:&|and)\\s*short[-\\s]?term\\s+investments?",
  "current\\s+(?:assets|ratio|tax\\s+expense)",
  "quick\\s+ratio",
  "(?:short|long)[-\\s]?term\\s+debt",
  "total\\s+debt(?:\\s*(?:\\/|to)\\s*ebitda)?",
  "net\\s+(?:cash\\s+flow\\s*(?:\\/|to)\\s*debt|debt)",
  "gross\\s+cash\\s+flow\\s*(?:\\/|to)\\s*debt",
  "debt\\s*(?:\\/|to)\\s*equity",
  "debt\\s+(?:issued|repaid)",
  "total\\s+assets(?:\\s+growth)?",
  "total\\s+(?:equity|liabilities)",
  "income\\s+tax\\s+payable",

  // Returns
  "return\\s+on\\s+(?:common\\s+)?equity",
  "return\\s+on\\s+assets",
  "return\\s+on\\s+invested\\s+capital",
  "\\b(?:roe|roa|roic)\\b",

  // Company, management, guidance, and qualitative research
  "acquisitions?",
  "auditors?",
  "business\\s+descriptions?",
  "(?:ceo|chief\\s+executive\\s+officer)",
  "(?:cfo|chief\\s+financial\\s+officer)",
  "(?:employee\\s+count|headcount|number\\s+of\\s+employees)",
  "fiscal\\s+year[-\\s]?end",
  "(?:headquarters|hq)",
  "largest\\s+business\\s+segment",
  "largest\\s+region\\s+by\\s+revenue",
  "legal\\s+proceedings?",
  "key\\s+risk\\s+factors?",
  "strategic\\s+priorities",
  "(?:demand|margin)\\s+outlook",
  "margin\\s+guidance",
  "earnings[-\\s]?call\\s+tone",
];

const TABULAR_TOPIC_SOURCE = `\\b(?:${TABULAR_TOPIC_PATTERNS.join("|")})\\b`;

export const TABULAR_TOPIC_TERM = new RegExp(TABULAR_TOPIC_SOURCE, "i");

/** Global-only copy used to remove topic phrases before aggregate-intent checks. */
export const TABULAR_TOPIC_TERM_GLOBAL = new RegExp(
  TABULAR_TOPIC_SOURCE,
  "gi",
);
